import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import moment from 'moment';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { Component } from 'react';
import type { MouseEvent, VoidFunctionComponent } from 'react';
import { Button, Col, ControlLabel, Form, FormControl, FormGroup, HelpBlock, ListGroup, ListGroupItem, Modal } from 'react-bootstrap';
import * as _ from 'lodash';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { window } from '../../globals';
import TeamsModule from '../../redux/teams';
import Icon, { IconText, icons } from '../generic/Icon';
import Loader from '../generic/Loader';
import { get as apiGet, post } from '../../redux/api';
import { useAddJobToNotificationQueue } from '../JobNotificationHub/Provider';
import type { TeamSelection } from '../../types';
import type { Task } from '../../../shared/model-typings';

type AttributionFileMetadata = {
  file: string;
  project: string;
  time: string | number;
  timeObj?: moment.Moment;
  type: 'PROJECT' | 'NOTICE'
};

type State = {
  loading?: boolean;
  reports?: AttributionFileMetadata[];
  error?: Error | null;
  config?: Record<string, string[]>;
  isCreatingReport?: boolean;
  isSubmittingReportJob?: boolean;
  reportTask?: Task | null;
};

type Props = {
  show?: boolean;
  onHide?(e?: MouseEvent<HTMLElement>): void;
  selectedTeams?: TeamSelection;
  onAddJobToNotificationQueue: (jobToken: string, jobName: string) => void;
};

class ModalBulkReport extends Component<Props, State> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      loading: false,
      reports: [],
      error: null,
      config: this.getDefaultConfig(),
      isCreatingReport: false,
      isSubmittingReportJob: false,
      reportTask: null,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!this.props.show && nextProps.show) {
      this.onLoad();
    }
  }

  onLoad = () => {
    this.setState({
      loading: true,
      reports: {},
      reportTask: null,
      isCreatingReport: false,
      config: this.getDefaultConfig(),
    });

    /* eslint-disable promise/always-return */
    /* eslint-disable promise/catch-or-return */
    apiGet<AttributionFileMetadata[]>('/api/organization/all_attribution_filenames')
      .then(({ data }) => {
        const normalizedReports = _.compact((data || []).map((report) => {
          // Old timestamps were epoch time. New timestamps are in the custom format. We can see which type this is by
          // checking for a '-'
          const formattedTime = (report.time as string).includes('-')
            ? moment(report.time, 'YYYY-MM-DD-HH-mmssSSSZ')
            : moment(new Date(Number(report.time)));

          if (!formattedTime.isValid() || !['PROJECT', 'NOTICE'].includes(report.type)) return undefined;

          report.timeObj = formattedTime;
          report.time = formattedTime.format('MMMM Do YYYY, h:mm a');
          return report;
        }));
        const sortedReports = _.sortBy(normalizedReports, (report) =>
          // report.timeObj should always exist, so the -1 should never be hit.
          report.timeObj ? -report.timeObj.valueOf() : -1,
        );
        this.setState({
          reports: sortedReports.map((report) => _.omit(report, 'timeObj')),
        });
      })
      .catch((error) => {
        this.setState({ error });
      })
      .finally(() => this.setState({ loading: false }));
    /* eslint-enable promise/always-return */
    /* eslint-enable promise/catch-or-return */
  };

  onSubmitReport = async (filename, email) => {
    this.setState({ isSubmittingReportJob: true });

    // 'all', undefined, or an empty array should select all teams, otherwise use the selected team(s)
    let teamId: TeamSelection | undefined | 'all' = this.props.selectedTeams;
    if (
      _.isNil(this.props.selectedTeams)
      || (Array.isArray(this.props.selectedTeams) && _.isEmpty(this.props.selectedTeams))
    ) {
      teamId = 'all';
    }

    const data = { filename, email, config: { ...this.state.config, teamId } };
    const response = await post<typeof data, Task>('/api/organization/attribution', data);
    const task = response.data;

    //  Add the job to the notification queue, so that it will be polled for completion.
    //  A toast will be shown to the user when the job is complete.
    if (task?.jobToken) {
      this.props.onAddJobToNotificationQueue(task.jobToken, task.task);
    }

    this.setState({ reportTask: response.data, isSubmittingReportJob: false });
  };

  getDefaultConfig() {
    return {
      columns: ['LIBRARY', 'AUTHORS', 'LICENSE', 'OTHERLICENSES'],
    };
  }

  $emailInput;

  renderContentPanel = () => {
    if (this.state.loading) {
      return (
        <div className="text-center">
          <Loader />
        </div>
      );
    }

    if (this.state.isCreatingReport) {
      return (
        this.state.reportTask ? (
          <div className="text-center">
            <img src="/img/success-modal-image.svg" width="100%" />
            <h4>Report Job Submitted</h4>
            <p>
              FOSSA will now proceed to collect data from across your organization<br />and send you an email when
              finished.
            </p>
            <Button onClick={this.props.onHide}>Finish</Button>
          </div>
        ) : (
          <div>
            <h3>Configure Package Report</h3>
            <br />
            <Form horizontal>
              <FormGroup>
                <Col componentClass={ControlLabel} sm={2}>
                  Format
                </Col>
                <Col sm={10}>
                  <FormControl componentClass="select" placeholder="CSV" disabled>
                    <option value="CSV">CSV</option>
                  </FormControl>
                </Col>
              </FormGroup>

              <FormGroup>
                <Col componentClass={ControlLabel} sm={2}>
                  Project Data
                </Col>
                <Col sm={5}>
                  {this.renderCheckField('Package')}
                  {this.renderCheckField('Authors')}
                  {this.renderCheckField('Description')}
                </Col>
                <Col sm={5}>
                  {this.renderCheckField('Package Homepage', 'PROJECTURL')}
                  {this.renderCheckField('Package Manager', 'SOURCE')}
                  {this.renderCheckField('Dependency Paths', 'DEPENDENCYPATHS')}
                  {this.renderCheckField('Issue Resolution Notes', 'ISSUENOTES')}
                </Col>
              </FormGroup>

              <FormGroup>
                <Col componentClass={ControlLabel} sm={2}>
                  License Detail
                </Col>
                <Col sm={5}>
                  {this.renderCheckField('License Template', 'LICENSE')}
                </Col>
                <Col sm={5}>{this.renderCheckField('Discovered License(s)', 'OTHERLICENSES')}</Col>
              </FormGroup>

              <FormGroup>
                <Col componentClass={ControlLabel} sm={2}>
                  Email
                </Col>
                <Col sm={10}>
                  <FormControl
                    type="email"
                    placeholder="Email"
                    ref={el => {
                      this.$emailInput = el;
                    }}
                    defaultValue={window.user.email}
                  />
                  <HelpBlock>The report will be sent to this address upon completion</HelpBlock>
                </Col>
              </FormGroup>
              <br />
              <div className="text-center">
                <Button bsStyle="link" onClick={() => this.setState({ isCreatingReport: false })}>
                  Cancel
                </Button>
                <Button
                  bsStyle="primary"
                  className="margin-left-md"
                  onClick={() => this.onSubmitReport(null, ReactDOM.findDOMNode(this.$emailInput).value)}
                  disabled={this.state.isSubmittingReportJob}
                >
                  <IconText
                    text="Submit Report Job"
                    icon={icons.plane}
                    alignIcon="right"
                  />
                </Button>
              </div>
            </Form>
          </div>
        )
      );
    }

    return (
      <div>
        {this.state.reports.length > 0 ? (
          <div>
            <Button bsStyle="primary" className="pull-right" onClick={() => this.setState({ isCreatingReport: true })}>
              <IconText
                text="New Report"
                icon={icons.add}
              />
            </Button>
            <h3>
              Report History{' '}
              <Button onClick={this.onLoad} bsStyle="link">
                <small>
                  <Icon icon={icons.reload} />
                </small>
              </Button>
            </h3>
            <ListGroup>
              {this.state.reports?.map(report => {
                const file_name = report.file;
                const display_name = report.type === 'NOTICE'
                  ? `Global Package Report - ${report.time}`
                  : `Project Report - ${report.project} - ${report.time}`;
                return (
                  <ReportListItem
                    file_name={file_name}
                    display_name={display_name}
                    onSendLink={this.onSubmitReport}
                    isSubmittingReportJob={this.state.isSubmittingReportJob}
                  />
                );
              })}
            </ListGroup>
          </div>
        ) : (
          <div className="text-center">
            <br/><br/>
            <h3>No Historical Reports</h3>
            <p>It looks like you haven't created any reports of this kind yet.</p>
            <Button bsStyle="primary" onClick={() => this.setState({ isCreatingReport: true })}>
              <IconText
                text="Start New Report"
                icon={icons.add}
              />
            </Button>
          </div>
        )}
      </div>
    );
  };

  renderCheckField = (label: string, f?: string) => {
    const checkKey = (f || label).toUpperCase();
    return (
      <div>
        <FormControlLabel
          control={
            <Checkbox
              disabled={this.state.isSubmittingReportJob}
              checked={this.state.config.columns.indexOf(checkKey) !== -1}
              onChange={() => {
                const config = this.state.config;
                const colIndex = config.columns.indexOf(checkKey);
                if (colIndex === -1) {
                  config.columns.push(checkKey);
                } else {
                  config.columns.splice(colIndex, 1);
                }
                this.setState({ config });
              }}
            />
          }
          label={label}
        />
      </div>
    );
  };

  render() {
    return (
      <Modal
        className="modal-bulk-report modal-horizontal"
        bsSize="large"
        show={this.props.show}
        backdrop
        animation
        onHide={this.props.onHide}>
        <div className="row">
          <div className="col-md-4 modal-horizontal-header">
            {this.state.isCreatingReport ? (
              <div className="padded">
                <a className="clickable" onClick={() => this.setState({ isCreatingReport: false })}>
                  &laquo; Back to Historical Reports
                </a>
                <br />
                <br />
                <h4>Create new report</h4>
                <br />
                <p>Configure what data you want FOSSA to collect about your packages.</p>
                <p>
                  Once you submit the request, FOSSA may take up to <b>10 minutes</b> to complete your report (depending
                  on the size of your organization's data).
                </p>
              </div>
            ) : (
              <div className="padded">
                <span className="icon-feature">
                  <Icon icon={icons.addToProject} />
                </span>
                <Modal.Title>{window.user.organization.title} Package Report</Modal.Title>
                <br />
                <p>
                  This report will collect and export data about every 3rd party packages used across your organization
                  and email you the results when finished.
                </p>
                <br />
                <Button bsStyle="primary" onClick={() => this.setState({ isCreatingReport: true })}>
                  <IconText
                    text="Start New Report"
                    icon={icons.add}
                  />
                </Button>
              </div>
            )}
          </div>
          <div className="col-md-8 modal-horizontal-content">
            <div className="padded">{this.renderContentPanel()}</div>
          </div>
        </div>
      </Modal>
    );
  }
}

export class ReportListItem extends Component<any, any> {
  constructor(props) {
    super(props);
    this.state = {
      isSendingLink: false,
    };
  }
  $emailInput;

  render() {
    return (
      <ListGroupItem>
        <span className="pull-right">
          {this.state.isSendingLink ? (
            <span>
              <input
                type="email"
                defaultValue={window.user.email}
                ref={el => {
                  this.$emailInput = el;
                }}
              />&nbsp;
              <Button onClick={() => this.setState({ isSendingLink: false })} bsSize="xsmall">
                x
              </Button>&nbsp;
              <Button
                onClick={() => {
                  this.props.onSendLink(this.props.file_name, this.$emailInput.value);
                  this.setState({ isSendingLink: false });
                }}
                bsSize="xsmall"
                bsStyle="primary"
                disabled={this.props.isSubmittingReportJob}>
                <Icon icon={icons.plane} />
              </Button>
            </span>
          ) : (
            <Button
              onClick={() => this.setState({ isSendingLink: true })}
              bsSize="xsmall"
              disabled={this.props.isSubmittingReportJob}
            >
              <IconText
                icon={icons.plane}
                text="Send New Link"
                alignIcon="right"
              />
            </Button>
          )}
        </span>
        <b>{this.props.display_name}</b>
      </ListGroupItem>
    );
  }
}

/**
 * HOC/inject the onAddJobToNotificationQueue function into the ModalBulkReport component.
 */
const ModalBulkReportWithAddJob: VoidFunctionComponent<
  Omit<Props, 'onAddJobToNotificationQueue'>
> = (props) => {
  const onAddJobToNotificationQueue = useAddJobToNotificationQueue();
  return (
    <ModalBulkReport
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      onAddJobToNotificationQueue={onAddJobToNotificationQueue}
    />
  );
};

const mapStateToProps = createStructuredSelector({
  selectedTeams: TeamsModule.selectors.getTeamSelection,
});

export default connect(mapStateToProps)(ModalBulkReportWithAddJob);
